const ADMIN_ROLES = new Set(["SUPER_ADMIN", "ADMIN"]);

function getPermissionsObject(me) {
  if (!me || typeof me !== "object") return {};
  return me.permissions && typeof me.permissions === "object"
    ? me.permissions
    : {};
}

export const isAdminUser = (me) => {
  if (!me) return false;
  return ADMIN_ROLES.has(String(me.roleCode || "").toUpperCase());
};

// 슈퍼어드민(운영자)은 모든 기능을 항상 본다 — 민트워치 등 계정별 토글도 우회.
export const isSuperAdminUser = (me) =>
  String(me?.roleCode || "").trim().toUpperCase() === "SUPER_ADMIN";

export const hasPermission = (me, key) => {
  if (!me) return false;
  if (isAdminUser(me)) return true;
  if (!key) return false;

  const permissions = getPermissionsObject(me);
  return !!permissions[key];
};

// 관리자 우회 없이 계정별 권한 플래그를 그대로 본다.
// 민트워치(메시지)처럼 "관리자여도 계정별로 끌 수 있어야 하는" 기능에 사용.
export const hasRawPermission = (me, key) => {
  if (!me || !key) return false;
  const permissions = getPermissionsObject(me);
  return !!permissions[key];
};

export const hasAnyPermission = (me, keys = []) => {
  if (!me) return false;
  if (isAdminUser(me)) return true;
  if (!Array.isArray(keys) || keys.length === 0) return false;

  return keys.some((key) => hasPermission(me, key));
};

export const getPermissionFlags = (me) => {
  const canDashboard = hasPermission(me, "CanDashboard");
  const canLiveView = hasPermission(me, "CanLiveView");
  const canPlayback = hasPermission(me, "CanPlayback");
  const canEvents = hasPermission(me, "CanEvents");
  // 민트워치 자격 = 슈퍼어드민(운영자) 또는 계정별 CanMessages(=민트워치 사용 권한).
  // 메시지·워치상태·SOS·심박수 등 모든 민트워치 관련 UI의 단일 게이트.
  const mintwatch = isSuperAdminUser(me) || hasRawPermission(me, "CanMessages");
  const canMessages = mintwatch;
  const canDevices = hasPermission(me, "CanDevices");
  const canWorkers = hasPermission(me, "CanWorkers");
  const canMap = hasPermission(me, "CanMap");
  const canLogs = hasPermission(me, "CanLogs");
  const canSettings = hasPermission(me, "CanSettings");

  // 워치/위험/SOS/심박 콘텐츠(장비·이벤트·대시보드·알림센터)도 민트워치 자격이 있어야 노출.
  // → 관리자여도 계정 민트워치가 꺼져 있으면 워치 관련이 전부 사라진다(슈퍼어드민 제외).
  const canWatchDanger =
    mintwatch &&
    hasAnyPermission(me, [
      "CanWatch",
      "CanWatchView",
      "CanWatchStatus",
      "CanWatchAlerts",
      "CanDanger",
      "CanDangerView",
      "CanSos",
      "CanHeartRate",
      "CanEmergency",
    ]);

  const canEventData =
    canEvents ||
    hasAnyPermission(me, [
      "CanEventLog",
      "CanEventView",
      "CanLogsView",
    ]);

  // 알림센터(종🔔·읽음처리)는 민트워치 위험 또는 별도 알림권한, 그리고
  // 이벤트 열람 권한이 있으면(=PPE 미착용 알림 등 이벤트 로그를 보는 계정) 사용.
  // → PPE 전용 현장(민트워치 off)의 지점/본사 관리자도 종·읽음처리가 보인다.
  const canAlertCenter =
    canWatchDanger ||
    canEventData ||
    hasAnyPermission(me, [
      "CanAlerts",
      "CanAlertCenter",
      "CanAlarm",
      "CanNotification",
      "CanWatchNotification",
    ]);

  return {
    isAdmin: isAdminUser(me),
    canDashboard,
    canLiveView,
    canPlayback,
    canEvents,
    canMessages,
    canDevices,
    canWorkers,
    canMap,
    canLogs,
    canSettings,
    canWatchDanger,
    canAlertCenter,
    canEventData,
  };
};